/// <reference types="vite/client" />
import { useEffect, useState } from "react";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  retainSearchParams,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { usePostHog } from "posthog-js/react";

import type { SessionAuth } from "@/common/gateway-session";
import type { BlogLocale } from "@/features/blog/locales";
import type { useUser } from "@/hooks/useUser";
import type { trpcQueryUtils } from "@/trpc/react";
import { checkHasSessionHint } from "@/common/gateway-session";
import {
  captureGclidFromUrl,
  fireSignupConversion,
  fireSubscriptionConversion,
  getStoredGclid,
  GOOGLE_ADS_CONVERSION_ID,
} from "@/common/google-ads";
import { checkIsConversionTrackingHost } from "@/common/google-ads-host";
import { BASE_URL, OG_VERSION, SOCIAL_LINKS } from "@/common/links";
import {
  checkIsSharedCacheable,
  checkIsSharedCachedPath,
  resolveFirstPaintSessionHint,
} from "@/common/session-first-paint";
import { rootSearchSchema, UTM_PARAMS } from "@/common/tracking-search";
import {
  buildWondatwinOrganizationJsonLd,
  buildWondatwinWebsiteJsonLd,
  checkIsWondatwinHost,
  WONDATWIN_DESCRIPTION,
  WONDATWIN_NAME,
  WONDATWIN_OG_IMAGE,
  WONDATWIN_ORIGIN,
  WONDATWIN_TITLE,
} from "@/common/wondatwin";
import { RouterContextSync } from "@/components/ClientProviders";
import { DefaultErrorComponent } from "@/components/DefaultErrorComponent";
import { Toaster } from "@/components/Toaster";
import { WebMCPRegistrar } from "@/components/WebMCPRegistrar";
import { BLOG_LOCALES } from "@/features/blog/locales";
import { useUser as useSessionUser } from "@/hooks/useUser";
import { CookieConsent } from "@/landing/components/CookieConsent";
import { RedirectBanner } from "@/landing/components/WondercatRedirectBanner";
import { HOME_HERO_SUBHEAD, HOME_META_TITLE } from "@/landing/lib/home-copy";
import { stewardFontLinks } from "@/landing/styles/steward-fonts";
import stewardThemeCss from "@/landing/styles/steward-theme.css?url";
import appCss from "@/styles/tailwind.css?url";
import { api } from "@/trpc/react";

type RouterContext = {
  api: typeof api;
  trpcQueryUtils: typeof trpcQueryUtils;
  auth: SessionAuth;
  user: ReturnType<typeof useUser>;
  posthog: ReturnType<typeof usePostHog>;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: async (): Promise<{
    isWondatwin: boolean;
    hasSessionHint: boolean;
  }> => {
    if (import.meta.env.SSR) {
      const { getCookie, getRequestHost, getRequestUrl, getResponseHeader } =
        await import("@tanstack/react-start/server");
      // Server-only module, behind the statically-eliminated SSR branch so it
      // never reaches the client bundle.
      const { WONDA_REFRESH_COOKIE, WONDA_SESSION_COOKIE } =
        await import("../../server/session/flow");

      const isSharedCached =
        checkIsSharedCachedPath(
          getRequestUrl({ xForwardedHost: true }).pathname,
        ) || checkIsSharedCacheable(getResponseHeader("Cache-Control"));

      const hasSessionHint =
        !isSharedCached &&
        resolveFirstPaintSessionHint({
          hasSessionCookie:
            getCookie(WONDA_SESSION_COOKIE) !== undefined ||
            getCookie(WONDA_REFRESH_COOKIE) !== undefined,
          cacheControl: getResponseHeader("Cache-Control"),
        });

      // The matching `Vary: Cookie` is set in server/middleware/session-vary,
      // because response headers are already on the wire by the time a loader
      // runs.
      return {
        isWondatwin: checkIsWondatwinHost(
          getRequestHost({ xForwardedHost: true }),
        ),
        hasSessionHint,
      };
    }
    return {
      isWondatwin: checkIsWondatwinHost(window.location.hostname),
      // Client-side navigations keep the returning-user paint; the live
      // session query supersedes it as soon as it settles.
      hasSessionHint: checkHasSessionHint(),
    };
  },
  validateSearch: zodValidator(rootSearchSchema),
  search: {
    middlewares: [retainSearchParams([...UTM_PARAMS])],
  },
  head: ({ loaderData }) => {
    const isWondatwin = loaderData?.isWondatwin === true;
    const title = isWondatwin ? WONDATWIN_TITLE : HOME_META_TITLE;
    const description = isWondatwin ? WONDATWIN_DESCRIPTION : HOME_HERO_SUBHEAD;
    const siteName = isWondatwin ? WONDATWIN_NAME : "Wonda";
    const siteUrl = isWondatwin ? WONDATWIN_ORIGIN : BASE_URL;
    const imageUrl = isWondatwin
      ? WONDATWIN_OG_IMAGE
      : `${BASE_URL}/og/.png?v=${OG_VERSION}`;

    return {
      meta: [
        { title },
        { charSet: "utf-8" },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1.0, viewport-fit=cover",
        },
        { name: "theme-color", content: "#F7F6F2" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: siteName },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { name: "description", content: description },
        { property: "og:url", content: siteUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { property: "og:image", content: imageUrl },
        { name: "twitter:image", content: imageUrl },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        // Steward is the app-wide theme: its token sheet loads globally so
        // root-mounted chrome (Toaster, CookieConsent, RedirectBanner) resolves
        // steward tokens on every route, including ones with their own design
        // system (agency). All its selectors are scoped, so it repaints nothing
        // by itself.
        { rel: "stylesheet", href: stewardThemeCss },
        ...stewardFontLinks,
        { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
        { rel: "icon", href: "/wonda-icon.svg", type: "image/svg+xml" },
        { rel: "apple-touch-icon", href: "/wonda-apple-touch-icon.png" },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Geist:wght@100..900&family=Geist+Mono:wght@100..900&display=swap",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            isWondatwin
              ? buildWondatwinOrganizationJsonLd()
              : {
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  "@id": `${BASE_URL}/#organization`,
                  name: "Wonda",
                  url: BASE_URL,
                  logo: `${BASE_URL}/wonda-icon.svg`,
                  description: HOME_HERO_SUBHEAD,
                  sameAs: [
                    SOCIAL_LINKS.github,
                    SOCIAL_LINKS.twitter,
                    SOCIAL_LINKS.linkedin,
                  ],
                },
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            isWondatwin
              ? buildWondatwinWebsiteJsonLd()
              : {
                  "@context": "https://schema.org",
                  "@type": "WebSite",
                  name: "Wonda",
                  url: BASE_URL,
                },
          ),
        },
        // Match Google's standard gtag snippet order: async src tag first so
        // the library download starts immediately, inline dataLayer setup second.
        // Consent defaults are still processed first because they're queued into
        // dataLayer before gtag.js finishes loading.
        ...(!isWondatwin && GOOGLE_ADS_CONVERSION_ID
          ? [
              {
                src: `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_CONVERSION_ID}`,
                async: true,
              },
              {
                children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500});gtag('js',new Date());gtag('config','${GOOGLE_ADS_CONVERSION_ID}');`,
              },
            ]
          : []),
      ],
    };
  },
  errorComponent: DefaultErrorComponent,
  component: RootComponent,
  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({
    select: (routerState) => routerState.location.pathname,
  });
  const lang = detectLocale(pathname);
  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function detectLocale(pathname: string): BlogLocale {
  const firstSegment = pathname.split("/")[1];
  return (BLOG_LOCALES as readonly string[]).includes(firstSegment ?? "")
    ? (firstSegment as BlogLocale)
    : "en";
}

function RootComponent() {
  const { isWondatwin } = Route.useLoaderData();
  const { from } = Route.useSearch();

  if (isWondatwin) {
    return (
      <>
        <Outlet />
        <Toaster />
        <CookieConsent />
      </>
    );
  }

  return (
    <RouterContextSync>
      {from && <RedirectBanner from={from} />}
      <Outlet />
      <GoogleAdsTracker />
      <Toaster />
      <CookieConsent />
      <WebMCPRegistrar />
    </RouterContextSync>
  );
}

// localStorage/sessionStorage can throw in sandboxed iframes, private-mode
// browsers, or under restrictive CSPs. Treat any failure as "no value" so
// a hostile environment can't crash the tracker and take the page down.
function safeStorageGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // ignore — dedup may re-fire next session but that's preferable to crashing
  }
}

/**
 * Client-side Google Ads integration:
 * - Captures `?gclid=` on first load
 * - Fires the Sign-up conversion the first time a newly created user appears
 * - Fires the New Subscription conversion when redirected back from Stripe
 * - Persists the gclid to the user's account after sign-up
 */
function GoogleAdsTracker() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  // The auth gateway serves this same SPA; firing there would double-count
  // every signup once the product host sees the same account.
  if (!checkIsConversionTrackingHost()) return null;
  return <GoogleAdsTrackerInner />;
}

function GoogleAdsTrackerInner() {
  const user = useSessionUser();
  // retry: 2 covers the brief lag between the account being provisioned and
  // the cached bearer reflecting it. Without it the first attempt can fail
  // UNAUTHORIZED and the gclid is lost permanently, breaking signup
  // attribution.
  const saveGclid = api.account.saveGclid.useMutation({ retry: 2 });

  // Capture gclid from URL on mount
  useEffect(() => {
    captureGclidFromUrl();
  }, []);

  // Fire sign-up conversion for newly created users + persist gclid.
  // Conversion dedup and gclid-saved dedup are separate flags so the Google
  // Ads pixel can fire on isSignedIn while saveGclid waits for accountId.
  const accountId = user.user?.publicMetadata.accountId;
  useEffect(() => {
    if (!user.isSignedIn || !user.user) return;
    const email = user.user.primaryEmailAddress?.emailAddress;
    if (!email) return;

    const createdAt = user.user.createdAt?.getTime() ?? 0;
    const isFreshSignup = Date.now() - createdAt < 60_000;
    if (!isFreshSignup) return;

    const userId = user.user.id;

    const convFlagKey = `wonda_signup_conv_fired_${userId}`;
    if (!safeStorageGet(localStorage, convFlagKey)) {
      void fireSignupConversion(email);
      safeStorageSet(localStorage, convFlagKey, "1");
    }

    // saveGclid is a protectedProcedure; defer until accountId lands. The
    // effect re-runs once provisioning flips it on.
    if (!accountId) return;

    const gclidFlagKey = `wonda_gclid_saved_${userId}`;
    const gclid = getStoredGclid();
    if (gclid && !safeStorageGet(localStorage, gclidFlagKey)) {
      saveGclid.mutate(
        { gclid },
        {
          onSuccess: () => safeStorageSet(localStorage, gclidFlagKey, "1"),
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isSignedIn, user.user?.id, accountId]);

  // Fire subscription conversion on Stripe success redirect.
  // Gate on isLoaded so we attach the user's email (for enhanced conversions)
  // rather than firing with email=undefined during the session's initial
  // hydration.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!user.isLoaded) return;
    const url = new URL(window.location.href);
    const isNewSub = url.searchParams.get("wonda_new_sub");
    const sid = url.searchParams.get("sid");
    if (!isNewSub || !sid) return;

    // Only Stripe Checkout sessions substitute `{CHECKOUT_SESSION_ID}`.
    // Billing Portal redirects (plan changes with an existing subscription)
    // and free-plan switches hand the literal placeholder back, so treat
    // that as a non-conversion and strip the params so we don't loop.
    if (sid === "{CHECKOUT_SESSION_ID}") {
      url.searchParams.delete("wonda_new_sub");
      url.searchParams.delete("sid");
      window.history.replaceState({}, "", url.toString());
      return;
    }

    const flagKey = `wonda_sub_conv_fired_${sid}`;
    if (safeStorageGet(sessionStorage, flagKey)) return;

    const email = user.user?.primaryEmailAddress?.emailAddress;
    void fireSubscriptionConversion({ transactionId: sid, email });
    safeStorageSet(sessionStorage, flagKey, "1");

    url.searchParams.delete("wonda_new_sub");
    url.searchParams.delete("sid");
    window.history.replaceState({}, "", url.toString());
    // user.user can be a fresh object reference across renders; depend on the
    // stable id so the effect doesn't churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoaded, user.user?.id]);

  return null;
}
